/**
 * components/object/PipeInspector.jsx — Iter 51.
 *
 * First-class operational pipe inspector. Reuses Iter 47 ObjectInspector
 * for the 4 tabs (Overview / Photos / Revisions / Timeline) — no
 * duplicate inspector framework. The header carries pipe-specific
 * engineering facts + station snapshot.
 *
 * Drawing-first: opens as a side drawer; never blocks the canvas.
 */
import React from "react";
import {
  X, GitBranch as PipeIcon, Ruler, Layers3, ArrowRightLeft,
} from "lucide-react";
import ObjectInspector from "@/components/object/ObjectInspector";
import ObjectRelations from "@/components/object/ObjectRelations";
import { formatPipeStation } from "@/lib/pipes";

export default function PipeInspector({ pipe, onClose }) {
  if (!pipe) return null;
  const station = formatPipeStation(pipe);
  const len = pipe.length_units != null
    ? `${Math.round(pipe.length_units)} ${pipe.units || "ft"}`
    : null;

  const header = (
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider"
          data-testid="pipe-header-meta">
      {pipe.pipe_type && (
        <span className="px-1.5 py-0.5 rounded border border-[var(--fop-border)] bg-[var(--fop-surface-2)] text-[var(--fop-text-2)]">
          <Layers3 size={9} className="inline -mt-0.5 mr-1" />
          {pipe.pipe_type}
        </span>
      )}
      {pipe.diameter_inches && (
        <span className="text-[var(--fop-text-2)]">
          <b className="text-[var(--fop-text)] font-semibold">{pipe.diameter_inches}"</b>
        </span>
      )}
      {pipe.material && (
        <span className="text-[var(--fop-text-2)]">{pipe.material}</span>
      )}
      {len && (
        <span className="text-[var(--fop-text-3)]">
          <Ruler size={9} className="inline -mt-0.5 mr-1" />
          {len}
        </span>
      )}
      {station && (
        <span className="w-full text-[var(--fop-text-2)]">
          <ArrowRightLeft size={9} className="inline -mt-0.5 mr-1 text-[var(--fop-primary)]" />
          <b className="text-[var(--fop-primary)]">{station}</b>
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="pipe-inspector"
          data-pipe-id={pipe.id}>
      <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-start gap-3">
        <PipeIcon size={14} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            Pipe Run
          </div>
          <div className="font-mono text-sm font-bold text-[var(--fop-text)] truncate"
                data-testid="pipe-inspector-name">
            {pipe.pipe_number || pipe.id}
          </div>
          <div className="text-[10px] font-mono text-[var(--fop-text-3)] truncate">
            {pipe.from_structure_id?.slice(-6)} →{" "}
            {pipe.to_structure_id?.slice(-6)}
          </div>
        </div>
        <button onClick={onClose}
                 className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] -mr-1"
                 aria-label="Close inspector" data-testid="pipe-inspector-close">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <ObjectInspector
          objectType="pipe" objectId={pipe.id}
          headerSlot={header}
        />
        <div className="border-t border-[var(--fop-border)] p-3">
          <ObjectRelations objectType="pipe" objectId={pipe.id} />
        </div>
      </div>
    </div>
  );
}
